import React, { useState, useEffect } from 'react';
import { AlertCircle, Save, X, Eye } from 'lucide-react';
import { ChristianTip, ChristianTipInput, isValidUrl, isValidCoverImage, getAgeRatingOptions } from '../lib/christian-tips';

interface ChristianTipFormProps {
  initialData?: ChristianTip;
  onSubmit: (data: ChristianTipInput) => Promise<void>;
  onCancel: () => void;
}

export default function ChristianTipForm({ initialData, onSubmit, onCancel }: ChristianTipFormProps) {
  const [formData, setFormData] = useState<ChristianTipInput>({
    type: 'livro',
    title: '',
    description: '',
    cover_image: '',
    link: '',
    age_rating: 'Livre',
    published_at: new Date().toISOString().split('T')[0],
    status: 'inativo'
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  const ageRatingOptions = getAgeRatingOptions();

  useEffect(() => {
    if (initialData) {
      setFormData({
        type: initialData.type,
        title: initialData.title,
        description: initialData.description,
        cover_image: initialData.cover_image,
        link: initialData.link || '',
        age_rating: initialData.age_rating,
        published_at: new Date(initialData.published_at).toISOString().split('T')[0],
        status: initialData.status
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Limpar erro do campo quando o usuário digita
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'O título é obrigatório';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'A descrição é obrigatória';
    } else if (formData.description.length > 500) {
      newErrors.description = 'A descrição deve ter no máximo 500 caracteres';
    }
    
    if (!formData.cover_image.trim()) {
      newErrors.cover_image = 'A URL da imagem de capa é obrigatória';
    } else if (!isValidUrl(formData.cover_image)) {
      newErrors.cover_image = 'URL inválida';
    } else if (!isValidCoverImage(formData.cover_image)) {
      newErrors.cover_image = 'A URL deve ser de uma imagem válida (JPG, PNG, etc.)';
    }
    
    if (formData.link && !isValidUrl(formData.link)) {
      newErrors.link = 'URL inválida';
    }
    
    if (!formData.published_at) {
      newErrors.published_at = 'A data de publicação é obrigatória';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-xl font-semibold text-brand-teal">
          {initialData ? 'Editar Recomendação' : 'Nova Recomendação'}
        </h2>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm flex items-center gap-1"
          >
            <Eye className="h-4 w-4" />
            {showPreview ? 'Ocultar Preview' : 'Mostrar Preview'}
          </button>
        </div>
      </div>

      {showPreview && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-medium text-gray-900 mb-2">Preview</h3>
          <div className="flex gap-4">
            <div className="w-32 h-48 bg-gray-200 rounded-lg overflow-hidden">
              {formData.cover_image && isValidCoverImage(formData.cover_image) ? (
                <img
                  src={formData.cover_image}
                  alt="Preview"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80';
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  Sem imagem
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs px-2 py-0.5 bg-brand-teal text-white rounded-full">
                  {formData.type === 'livro' ? 'Livro' : 'Filme'}
                </span>
                <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full">
                  {formData.age_rating}
                </span>
                <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full">
                  {formData.status === 'ativo' ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              <h4 className="font-medium text-brand-teal">{formData.title || 'Título da recomendação'}</h4>
              <p className="text-sm text-gray-600 mt-1 line-clamp-3">
                {formData.description || 'Descrição da recomendação...'}
              </p>
              {formData.link && (
                <a
                  href={formData.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-teal hover:underline text-sm mt-2 inline-block"
                >
                  {formData.type === 'livro' ? 'Comprar' : 'Assistir'}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Tipo */}
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Conteúdo *
            </label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
            >
              <option value="livro">Livro</option>
              <option value="filme">Filme</option>
            </select>
          </div>
          
          {/* Título */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Título *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`w-full px-4 py-2 border ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal`}
              placeholder="Título completo"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-500">{errors.title}</p>
            )}
          </div>
          
          {/* Descrição */}
          <div className="md:col-span-2">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Descrição * <span className="text-gray-500">({formData.description.length}/500 caracteres)</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              className={`w-full px-4 py-2 border ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal`}
              placeholder="Descrição detalhada (máximo 500 caracteres)"
              maxLength={500}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-500">{errors.description}</p>
            )}
          </div>
          
          {/* URL da Imagem */}
          <div className="md:col-span-2">
            <label htmlFor="cover_image" className="block text-sm font-medium text-gray-700 mb-1">
              URL da Imagem de Capa *
            </label>
            <input
              type="url"
              id="cover_image"
              name="cover_image"
              value={formData.cover_image}
              onChange={handleChange}
              className={`w-full px-4 py-2 border ${
                errors.cover_image ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal`}
              placeholder="https://exemplo.com/imagem.jpg (300x450px)"
            />
            {errors.cover_image ? (
              <p className="mt-1 text-sm text-red-500">{errors.cover_image}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                Dimensões recomendadas: 300x450px. Formatos: JPG, PNG.
              </p>
            )}
          </div>
          
          {/* Link */}
          <div className="md:col-span-2">
            <label htmlFor="link" className="block text-sm font-medium text-gray-700 mb-1">
              Link para Compra/Streaming
            </label>
            <input
              type="url"
              id="link"
              name="link"
              value={formData.link}
              onChange={handleChange}
              className={`w-full px-4 py-2 border ${
                errors.link ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal`}
              placeholder="https://exemplo.com/comprar"
            />
            {errors.link && (
              <p className="mt-1 text-sm text-red-500">{errors.link}</p>
            )}
          </div>
          
          {/* Classificação Etária */}
          <div>
            <label htmlFor="age_rating" className="block text-sm font-medium text-gray-700 mb-1">
              Classificação Etária *
            </label>
            <select
              id="age_rating"
              name="age_rating"
              value={formData.age_rating}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
            >
              {ageRatingOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Data de Publicação */}
          <div>
            <label htmlFor="published_at" className="block text-sm font-medium text-gray-700 mb-1">
              Data de Publicação *
            </label>
            <input
              type="date"
              id="published_at"
              name="published_at"
              value={formData.published_at}
              onChange={handleChange}
              className={`w-full px-4 py-2 border ${
                errors.published_at ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal`}
            />
            {errors.published_at && (
              <p className="mt-1 text-sm text-red-500">{errors.published_at}</p>
            )}
          </div>
          
          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status *
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
            >
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
          </div>
        </div>
        
        {Object.keys(errors).length > 0 && (
          <div className="mt-4 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Corrija os erros abaixo:</p>
              <ul className="list-disc list-inside text-sm mt-1">
                {Object.values(errors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        
        <div className="mt-6 flex justify-end gap-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="bg-brand-teal text-white px-6 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Salvando...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Salvar</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}